// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The machine roster (M3-37, M10-20, G1-04, G1-05; 08 §2).
//
// **The catalogue is the server's, and the bundle is the floor under it** (G1-04). This page used
// to build its tiles from the one definition in the bundle, at module load, with a comment saying
// that the roster would arrive one day; the Builder could publish a machine, `GET /machines` would
// list it, the leaderboard would rank it, and this page could not show it. It now draws what the
// API lists, and falls back to the bundled machine when there is no session, no network or no
// answer — which is not a degraded mode but the ordinary state of a stranger's first visit.
//
// The bundled tile is always present and always first. It is the one machine this product can
// produce with no connection at all, and a roster that replaced it with whatever the server
// happened to say would make the offline arcade empty.
//
// The tiles are read from the definitions themselves where a definition is in hand, so a machine
// that exists is a machine that appears. 08 §2 wants each tile "running live attract" — that needs
// a WebGL context per tile and is G1-12; with one machine the tile is a link to the machine that
// is *already* running full-screen on the front door, and a second copy of it would be two
// simulations for one player.
//
// WHY LOCKED MACHINES ARE SHOWN AND NOT HIDDEN (M10-20)
// -----------------------------------------------------
// 01 §6 put every machine but NEON NOVA behind Pro on 2026-08-24, which raises the question this
// file has to answer: does a free player see the rest of the roster at all?
//
// **Yes, badged.** Browsing is the upsell, and it is the only one this product has that is honest —
// nobody wants a subscription in the abstract, they want *that machine*. A roster filtered down to
// what you already own is a product that looks finished and small; the same roster with four locked
// tiles on it is a product that looks larger than what you are holding, which is the truth. The
// server agrees: `GET /machines` is public and ungated, and the paywall lands on the definition
// fetch — so this page is drawing exactly what the API is willing to list.
//
// It is also the arrangement the native launcher already had. `LauncherModel.AvailabilityOf`
// returns `Playable` or `NeedsPro` per roster entry and the Quest tiles have always been badged
// from it; the web was the odd one out.
//
// **The guest machine is badged differently from the free one**, because "free this week" and
// "free" are different promises and a player who found out the hard way that one of them had a
// deadline would be right to be annoyed.
//
// WHY A BUILD CARD SITS IN THE ROSTER (M10-24)
// --------------------------------------------
// The roster is where somebody looks for a machine, and the Machine Builder is where the machine
// they did not find gets made. That is the argument above carried one step further back: nobody
// wants a subscription in the abstract, and a person reading a one-tile arcade is being shown very
// plainly how short the list is.
//
// **The upsell is not the reason the card is here, though.** `NAV_TABS` marks the Build tab
// `essential: false`, and `tabsFor` drops every non-essential tab at or below `NARROW_MAX_PX`. On a
// phone narrower than 400 CSS pixels the Builder therefore had **no entry point anywhere in the
// product** — the tab is gone and nothing else linked to it. This card is the way in, which makes
// it a reachability fix that happens to also sell something.
//
// **It never leads to the account page, and that is the one place it departs from the rule above.**
// A locked *machine* tile goes to `/account?want=…` because there is nothing behind it to show: the
// definition fetch is gated and the machine will not run. The Builder is the opposite. Nothing gates
// the route, anybody may design and test-play, and what Pro buys is the machine surviving the
// reload — so sending a free player to a price instead of into the tool would throw away the only
// demonstration this product has of what a subscription is *for*. `PublishPanel` renders the trade
// as a sandbox notice at the top of the workspace, before an author has invested anything, which is
// what keeps the open door from being a trap sprung at the Save button an hour in.
//
// **It is drawn as an action rather than as a machine.** A card with a name, a tagline and a device
// count is a thing to play, and a person taps it expecting a ball. This one carries a plus, an
// accent border and no tagline — and deliberately *not* the dashed border a locked tile wears,
// since that stroke already means "you cannot have this" and it means the opposite here.

import { Link } from 'react-router'
import { CopySections } from '../components/CopySections'
import { PAGE_COPY } from '../../seo/pageCopy'
import { GUIDES_HEADING, guideLinkText, guidePath } from '../../seo/machineAbout'
import { MACHINE_GUIDES } from '../machineGuides'
import machineJson from '../../../machines/neon-nova/machine.json?raw'
import { loadMachine } from '@core/machine'
import { FREE_FLAGSHIP_SLUG } from '@server/proGate'
import { gateVerdict } from '../../ui/pricingCatalog'
import { knownTier } from '../entitlementSignal'
import { useLocalisation } from '../i18n/useLanguage'
import type { RosterMachine } from '../machineSource'
import { useRoster } from '../useMachineFeed'
import { AttractProvider, TileAttract } from '../components/TileAttract'
import { contentKey } from '../attractStore'

/** How a tile is offered. Mirrors the native `MachineAvailability`. */
export type Availability = 'free' | 'free-this-week' | 'playable' | 'needs-account' | 'needs-pro'

interface Tile {
  readonly id: string
  readonly slug: string
  readonly title: string
  readonly tagline: string
  /**
   * How many devices the playfield has, when that is knowable.
   *
   * **Absent for a machine listed but not downloaded, and that is the honest answer.** The roster is
   * deliberately small — `GET /machines` carries a name, an era and a content hash so that opening
   * this page is one cheap request — and a device count is a fact about the *definition*, which is
   * tens of kilobytes this page has no reason to fetch. A count invented from the era would be a
   * number a player could compare against the machine and find wrong.
   */
  readonly devices: number | undefined
  /** The era, shown where a device count is not known. Both are "what kind of machine is this". */
  readonly era: string | undefined
  /**
   * The version and content hash this tile's art is asked for by (M8-79).
   *
   * Both come off the roster row and both used to be dropped here. `undefined` for the bundled
   * machine, which carries its own art and asks nobody.
   */
  readonly semver: string | undefined
  readonly definitionHash: string | undefined
  /**
   * Where this tile'''s finished picture is filed (M8-85).
   *
   * Set only for the bundled machine, which has no hash to file it under and is also the tile drawn
   * earliest — before the roster answers — so it cannot wait for one either.
   */
  readonly pictureKey?: string | undefined
  readonly to: string
}

/**
 * The catalogue, read once at module load.
 *
 * At load rather than per render: parsing and validating a machine definition is real work, and a
 * roster that re-parsed every machine on every keystroke of a future search box would be a page
 * that got slower the more machines the Builder produced.
 */
const BUNDLED_TILE: Tile = tileFor(machineJson)

/**
 * The one definition this page holds, for the one tile that can animate today (G1-12).
 *
 * Loaded once beside the tile it belongs to. A roster row is a name, an era and a hash and there is
 * nothing in it to draw; a tile animates when its definition is in hand, which today means the
 * bundled machine and tomorrow means one a player has opened before. **Fetching sixty kilobytes per
 * tile to paint a thumbnail would make browsing the most expensive thing in the product**, which is
 * the trade `attractTiles.ts` records and refuses.
 */
const BUNDLED_DEFINITION = loadMachine(JSON.parse(machineJson) as unknown).definition

function tileFor(raw: string): Tile {
  const { definition } = loadMachine(JSON.parse(raw) as unknown)
  return {
    id: definition.machineId,
    // The definition has no `slug` of its own — `machineId` *is* the slug, and the flagship's is
    // `neon-nova`, which is the constant the server gate compares against. Read from the document
    // rather than assumed, so a renamed machine renames its own tile.
    slug: definition.machineId,
    title: definition.meta.title,
    tagline: definition.meta.subtitle ?? '',
    devices: definition.devices.length,
    era: definition.meta.era,
    // The bundle carries its art in the same document, so there is nothing to ask for and no hash
    // to ask with — `bundledMachine` says the same thing about the same document for the same
    // reason, and a made-up value here would be a string that looks like a hash.
    semver: undefined,
    definitionHash: undefined,
    // ...but its finished *picture* still has to be filed somewhere (M8-85), and that is a local
    // question rather than a wire one, so it gets a key of its own with a prefix that says so.
    // Derived from the document's own bytes, which the page is already holding as a string.
    pictureKey: contentKey(raw),
    to: `/play/${definition.machineId}`,
  }
}

/**
 * The tiles to draw, from the roster where there is one and from the bundle where there is not.
 *
 * **The bundled machine is always in the list and always first**, whatever the roster says. It is
 * the one machine that plays with no connection, and a page that replaced it with the server's
 * answer would be an empty arcade for anybody offline. Where the roster names it too, the bundled
 * tile wins: it is the same machine and this copy knows its subtitle and its device count.
 *
 * Exported for its test. It is the whole of what G1-04 changed and it is a pure function of two
 * arguments, which is the shape that can be asserted without a server.
 */
export function tilesFor(roster: readonly RosterMachine[] | undefined): readonly Tile[] {
  if (roster === undefined) return [BUNDLED_TILE]
  const rest = roster
    .filter((row) => row.slug !== BUNDLED_TILE.slug)
    .map(
      (row): Tile => ({
        id: row.currentVersionId,
        slug: row.slug,
        title: row.title,
        // No subtitle in the roster, and none invented: a tagline made up here is copy the machine's
        // author never wrote, shown under their name.
        tagline: '',
        devices: undefined,
        era: row.era,
        semver: row.currentVersion,
        definitionHash: row.definitionHash,
        to: `/play/${row.slug}`,
      }),
    )
  return [BUNDLED_TILE, ...rest]
}

/**
 * How this tile is offered to this viewer.
 *
 * **The same `gateVerdict` the rest of the product uses**, rather than a second opinion about who
 * may play what. The tier comes from `entitlementSignal`, which is a hint and says so — a forged
 * value here buys somebody an unlocked-looking tile and a 402 the moment they open it, which is
 * exactly the failure the signal's own header describes and declines to build a second gate for.
 *
 * NEON NOVA and the week's guest are answered before the gate is asked at all: they are free to
 * everybody including a visitor with no account, so running them through a capability check would
 * be asking a question whose answer is already written on the machine.
 */
export function availabilityOf(
  tile: Pick<Tile, 'slug'>,
  guestSlug: string | null,
  tier: ReturnType<typeof knownTier>,
): Availability {
  if (tile.slug === FREE_FLAGSHIP_SLUG) return 'free'
  if (guestSlug !== null && tile.slug === guestSlug) return 'free-this-week'
  // Unknown tier is a cold tab that has never asked. Treated as locked rather than as open: a badge
  // that vanishes a second after the page settles is worse than one that was right to begin with,
  // and the machine behind it is genuinely not playable without a subscription.
  const verdict = gateVerdict('full-roster', tier ?? 'anonymous', false)
  if (verdict === 'allowed') return 'playable'
  // **"No account" and "no subscription" are two different sentences, and only one of them is
  // actionable by the person reading it** (M8-78). `gateVerdict` cannot make this distinction here:
  // it answers `needs-account` only for a capability a *free account* would have given, and
  // `full-roster` is Pro-only, so a visitor with no account was told "Pro" — true, and not the
  // step in front of them. A person who has never signed in cannot subscribe either; the first
  // thing they have to do is have an account, and the tile now says so.
  //
  // **Both of the no-account answers, which is a distinction this file already had to get right in
  // the other direction.** `entitlementSignal` says it in its own words: *"never asked is a cold tab,
  // and anonymous is a visitor who has asked and has no account."* Two values, one meaning here —
  // and testing only `undefined` would have left the visitor who has actually been asked about,
  // which is most of them by the second page, reading the sentence written for a subscriber.
  return tier === undefined || tier === 'anonymous' ? 'needs-account' : 'needs-pro'
}

/**
 * How the build card is offered to this viewer (M10-24).
 *
 * **`builder-private`, not `full-roster`.** They are two rows in 01 §6 and they move independently
 * — the Builder went behind the subscription on 2026-08-24 and the roster had been there since the
 * beginning — so a card that asked the roster's question would badge itself wrongly the first time
 * one of them changed without the other.
 *
 * `builder-private` is Pro-only, which is why the answer is only ever `playable` or `needs-pro`:
 * `gateVerdict` returns `needs-account` only for a capability a *free account* would have given, and
 * an account alone buys nothing on this row. That two-way answer is what lets the card share
 * `BADGES` with the machines, and with it the rule that a subscriber is shown no badge at all.
 */
export function builderAvailability(tier: ReturnType<typeof knownTier>): Availability {
  return gateVerdict('builder-private', tier ?? 'anonymous', false) === 'allowed'
    ? 'playable'
    : 'needs-pro'
}

export function Arcade(): React.JSX.Element {
  const { t } = useLocalisation()
  const tier = knownTier()
  /**
   * The catalogue and the week's guest machine, both from the server (G1-04, G1-05).
   *
   * **`freeMachineSlug` takes effect with no client release**, which is the whole of FR-022 and the
   * reason it is a server field rather than a constant: setting `pinball.app_config
   * .free_machine_slug` to a second machine badges its tile `free-this-week` and lets an anonymous
   * visitor play it, and clearing it puts the badge back. This value used to be a hard `null` with
   * a comment saying it would become the bootstrap value one day.
   *
   * `undefined` is *not yet known* rather than *none*, and `availabilityOf` is given `null` for it —
   * a tile badged free for a second and then locked is worse than one that was right to begin with,
   * which is the same argument that file already makes about an unknown tier.
   */
  /**
   * **Every published machine, whether or not this browser has an account** (G1-04, M8-78).
   *
   * This used to be `useRoster(tier !== undefined)` — the roster was fetched only once
   * `localStorage` held a tier, so a visitor who had never signed in saw one tile. The paragraph
   * that argued for it ended: *"Worth re-reading when there is a second machine to be locked."*
   * IRON FATHOM was published on 2026-09-03, and re-reading it turned the trade the other way for a
   * reason that paragraph had not weighed: what a stranger could not see was not a **locked** tile,
   * it was `free_machine_slug`'s **free-this-week** machine — free to play, with no account, by
   * anyone. The weekly rotation exists for exactly that visitor and they were the one person it was
   * hidden from.
   *
   * **FR-030 is intact, and it is worth being precise about which half.** The clause is that a
   * visitor plays with no account and **no server identity**; `GET /machines` is `auth: 'public'`,
   * carries no session and no token, and returns rows any crawler may read. What it costs is one
   * anon-key request from a page a visitor navigated to deliberately — not from the front door and
   * not from `/play/:machineId`, both of which stay network-free and are still asserted so by
   * `arcade.spec.ts`. `landing.spec.ts` names this one call rather than dropping its assertion, so
   * a *second* request from this page is still a failure.
   */
  const { roster, freeMachineSlug, loading } = useRoster(true)
  const guestSlug: string | null = freeMachineSlug ?? null
  const tiles = tilesFor(roster)
  // Asked once for the whole page rather than inside the card, so the badge and the link title are
  // answering the same question — two `gateVerdict` calls is two chances to disagree about a viewer.
  const buildOffer = builderAvailability(tier)
  const buildBadge = BUILD_BADGES[buildOffer]

  return (
    <div className="page">
      {/* The page says what it is (S3-04). The top bar says which tab you are on, which is a
          different sentence and was standing in for this one. */}
      <h1>{t('arcade.heading')}</h1>
      <p className="page__lede">{t('arcade.lede')}</p>
      {/*
        The roster is where somebody meets a locked tile, so it is where "what does Pro cost" is
        actually asked (S5-02). The locked tiles themselves go to the account page, which is where
        the offer is resolved; this is the answer for somebody who wants to read the price first.
      */}
      <p className="page__lede">
        <Link to="/pricing">{t('arcade.pricing-link')}</Link>
      </p>
      {guestSlug !== null ? <p className="page__lede">{t('arcade.guest-lede')}</p> : null}
      {/*
        Said once, quietly, while the catalogue is on its way — and never as a blocking state. The
        bundled machine is already drawn below, so a visitor can start playing before this settles;
        a page that waited would be a page that is slower than the one it replaced for the only
        machine most visitors ever open.
      */}
      {loading ? (
        <p className="page__lede" role="status" aria-live="polite">
          {t('arcade.loading')}
        </p>
      ) : null}
      {/*
        One loop, one painter and one observer for the whole grid (G1-12). Around the roster rather
        than inside each tile, because the thing that has to be shared is the *scheduler*: a tile
        that owned its own timer would be one wake-up per tile and the cap would be a cap on
        nothing. It runs no loop at all when nothing is animating, which is every visit under
        `prefers-reduced-motion` and every visit with the tab in the background.
      */}
      <AttractProvider>
      <div className="roster">
        {tiles.map((tile) => {
          const availability = availabilityOf(tile, guestSlug, tier)
          const locked = availability === 'needs-pro' || availability === 'needs-account'
          return (
            <Link
              key={tile.id}
              // **A locked tile still leads somewhere, and it is not the machine.** A disabled tile
              // teaches nobody what it is for and gets tapped twice; this one goes to the account
              // page, which resolves the entitlement and shows the offer in the one place a person
              // came to ask about their account (M10-16).
              to={locked ? `/account?want=full-roster&machine=${tile.slug}` : tile.to}
              className={locked ? 'tile tile--locked' : 'tile'}
              // **Three sentences, because there are three viewers** (M8-78). "{title} is part of
              // Pro" is the right answer for a signed-in free player and the wrong one for somebody
              // with no account at all, whose next step is not a card — it is having an account.
              // The badge still says Pro either way: that is a fact about the machine, and a badge
              // reading "Sign in" would promise something an account does not buy.
              title={
                availability === 'needs-account'
                  ? t('arcade.signin-title', { title: tile.title })
                  : locked
                    ? t('arcade.locked-title', { title: tile.title })
                    : t('arcade.play-title', { title: tile.title })
              }
            >
              {/*
                The bundled machine draws from the definition in hand; every other tile is given the
                two strings that ask for its art, and the provider does the asking for the few that
                would animate (M8-79). **A locked tile is given them too** — the art route is
                ungated, and a tile that shows what the machine looks like is the offer this page is
                making. One that shows only a name is asking somebody to subscribe to a word.
              */}
              <TileAttract
                machineId={tile.slug}
                definition={tile.slug === BUNDLED_TILE.slug ? BUNDLED_DEFINITION : undefined}
                semver={tile.semver}
                definitionHash={tile.definitionHash}
                pictureKey={tile.pictureKey}
                title={tile.title}
              />
              <span className="tile__name">{tile.title}</span>
              {tile.tagline === '' ? null : <span className="tile__meta">{tile.tagline}</span>}
              {/*
                The device count where the definition is in hand, the era where it is not. Both
                answer "what kind of machine is this", and neither is guessed: a count derived from
                an era would be a number a player could check against the machine and find wrong.
              */}
              <span className="tile__meta">
                {tile.devices === undefined
                  ? t(eraKey(tile.era))
                  : t('arcade.devices', { count: tile.devices })}
              </span>
              {BADGES[availability] === null ? null : (
                <span className={BADGES[availability]!.className}>{t(BADGES[availability]!.key)}</span>
              )}
            </Link>
          )
        })}
        {/*
          The build card, last in the grid.

          Last rather than first, because the roster's job is the machines and an action that jumps
          the queue in front of them reads as an advertisement rather than as an option. It is also
          the order somebody arrives at it in: you look for a machine, you do not find it, you make
          one.

          **Always `/builder`, whatever the badge says.** See the header — this is the one card in
          the grid whose destination does not change when it is locked, because the thing behind it
          works for everybody and only *keeping* what you make is what Pro sells.
        */}
        <Link
          to="/builder"
          className="tile tile--build"
          title={buildOffer === 'needs-pro' ? t('arcade.build.title-pro') : t('arcade.build.title')}
        >
          {/*
            A character, not an icon file — there is not one image in this bundle and this is not
            going to be the first. Hidden from assistive technology because the card's own name says
            what it does, and "plus" announced in front of it is noise.
          */}
          <span className="tile__plus" aria-hidden="true">
            +
          </span>
          <span className="tile__name">{t('arcade.build.name')}</span>
          <span className="tile__meta">{t('arcade.build.meta')}</span>
          {buildBadge === null ? null : (
            <span className={buildBadge.className}>{t(buildBadge.key)}</span>
          )}
        </Link>
      </div>
      </AttractProvider>
      {/*
        A guide per machine we ship (S3-10), under the roster rather than inside a tile: a tile is
        one link already, and a link inside a link is not a thing HTML allows. Written from the
        build's own list, and the prerenderer writes the same list under the same heading — so the
        guides are linked from a page a crawler reads, not only from the sitemap.
      */}
      {MACHINE_GUIDES.length === 0 ? null : (
        <nav className="page copy copy--below" aria-label={GUIDES_HEADING}>
          <h2>{GUIDES_HEADING}</h2>
          <ul className="copy__list">
            {MACHINE_GUIDES.map((guide) => (
              <li key={guide.id}>
                <Link to={guidePath(guide.id)} title={guideLinkText(guide.title)}>
                  {guideLinkText(guide.title)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
      {/*
        The words this page is about (S3-07). Under the roster, because the roster is what somebody
        came for — and in the document, because until 2026-09-06 this page offered a crawler
        twenty-one words and the privacy policy offered seven hundred and thirty.
      */}
      <CopySections sections={PAGE_COPY['/arcade']?.below} />
    </div>
  )
}

/**
 * The era, as a sentence, for a tile whose definition has not been downloaded.
 *
 * A closed mapping over the format's four eras, with a fallback for a fifth that a later format
 * adds — this build would otherwise show a raw `lcd-modern` to a player, which is a slug and not a
 * sentence. `MessageKey` is the return type, so an era added here without copy does not compile.
 */
function eraKey(era: string | undefined): 'arcade.era.em' | 'arcade.era.ss' | 'arcade.era.dmd' | 'arcade.era.lcd' | 'arcade.era.unknown' {
  switch (era) {
    case 'em-1970s':
      return 'arcade.era.em'
    case 'ss-1980s':
      return 'arcade.era.ss'
    case 'dmd-1990s':
      return 'arcade.era.dmd'
    case 'lcd-modern':
      return 'arcade.era.lcd'
    default:
      return 'arcade.era.unknown'
  }
}

/**
 * What each availability says on the tile, and what it is styled as.
 *
 * `playable` carries **no badge at all**: a Pro subscriber looking at a roster where every tile is
 * stamped "Pro" is being sold something they have already bought, which is the same mistake the
 * subscription panel refuses to make by never showing them a price.
 *
 * **The class name is written out whole rather than assembled.** `'tile__badge--' + availability`
 * reads fine and is invisible to the check that every class the client writes has a rule behind it
 * — and a class name that matches no rule is a string, so nothing else would have caught it
 * either. Four literals, four selectors, one test that can see both.
 */
const BADGES: Readonly<
  Record<
    Availability,
    | { readonly key: 'arcade.badge.pro' | 'arcade.badge.free' | 'arcade.badge.free-this-week'; readonly className: string }
    | null
  >
> = {
  free: { key: 'arcade.badge.free', className: 'tile__badge tile__badge--free' },
  'free-this-week': {
    key: 'arcade.badge.free-this-week',
    className: 'tile__badge tile__badge--guest',
  },
  playable: null,
  // **Still "Pro", because that is what is true of the machine.** The badge describes the machine
  // and the title describes the next step: a badge reading "Sign in" would tell a visitor that an
  // account unlocks the roster, which it does not, and this file already refuses to build a paywall
  // that says something cheaper than the truth.
  'needs-account': { key: 'arcade.badge.pro', className: 'tile__badge tile__badge--pro' },
  'needs-pro': { key: 'arcade.badge.pro', className: 'tile__badge tile__badge--pro' },
}

/**
 * The build card's badge, which is deliberately **not** the roster's (M10-25).
 *
 * M10-24 gave the card `BADGES`, and with it the rule that a subscriber sees nothing — right — and
 * the word a locked viewer sees, which was wrong from the first day. Every machine tile wearing
 * `Pro` is a tile that leads to a page asking for money, because the definition behind it is gated
 * and the machine will not run. This card leads into a workspace that opens for anybody: 01 §6's
 * *"the Builder is a sandbox, not a locked door"*, and M10-24's own note that it goes to `/builder`
 * whatever the badge says.
 *
 * So the card was advertising a padlock on the one door in the grid that has none — and a badge is
 * taken in before a `title` is read, which is where the card's honest sentence lived. *"Free to
 * try"* is the same fact the title has always stated, in the place a reader actually looks.
 *
 * It stays a `Record<Availability, …>` and shares the shape rather than the values, so the two
 * cannot drift into different kinds of thing.
 */
const BUILD_BADGES: Readonly<
  Record<Availability, { readonly key: 'arcade.badge.try'; readonly className: string } | null>
> = {
  free: null,
  'free-this-week': null,
  // A subscriber is shown no badge at all, exactly as `BADGES` does it: they are not trying
  // anything, they own it.
  playable: null,
  'needs-account': { key: 'arcade.badge.try', className: 'tile__badge tile__badge--try' },
  'needs-pro': { key: 'arcade.badge.try', className: 'tile__badge tile__badge--try' },
}
